import { ApolloClient, ApolloLink, fromPromise, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";

import { broadcastLogout } from "@/contexts";
import { clearAuthTokens, getRefreshToken } from "@/contexts/AuthContext/AuthContext.utils";
import { env } from "@/env";
import { addSentryBreadcrumb, captureSentryException } from "@/lib/sentry";
import {
  extractServerErrorInfo,
  getGraphQLErrorFingerprint,
  getServerErrorFingerprint,
  isExpectedGraphQLError,
} from "@/lib/sentryGraphql";
import {
  clearImpersonationSession,
  getImpersonationToken,
  isImpersonating,
} from "@/utils/auth/impersonation";
import { getValidAccessToken, refreshAccessToken } from "@/utils/auth/tokenRefresh";

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        //  * NEED TO DEFINE WHAT TYPE OF DATA WILL BE MERGED
        alltournament: {
          merge: (existing: any, incoming: any) => incoming,
        },
        leagues: {
          merge: (existing: any, incoming: any) => incoming,
        },
        categories: {
          merge: (existing: any, incoming: any) => incoming,
        },
        tournamentCategories: {
          merge: (existing: any, incoming: any) => incoming,
        },
        federations: {
          merge: (existing: any, incoming: any) => incoming,
        },
        partners: {
          merge: (existing: any, incoming: any) => incoming,
        },
        licenses: {
          merge: (existing: any, incoming: any) => incoming,
        },
        purchasedLicenses: {
          merge: (existing: any, incoming: any) => incoming,
        },
        teams: {
          merge: (existing: any, incoming: any) => incoming,
        },
        rankingPoints: {
          merge: (existing: any, incoming: any) => incoming,
        },
        platformUsers: {
          merge: (existing: any, incoming: any) => incoming,
        },
      },
    },
  },
});

const URL = `${env.backendUrl}/graphql`;
// const URL = "https://tornated-dev-094f13c08ec4.herokuapp.com/graphql";
// const URL = "https://staging-api.tournated.com/graphql"; // STAGING
// const URL = "https://api-dev.tournated.com/graphql"; // DEV
// const URL = "http://localhost:3000/graphql"; // local
// const URL = "https://4fr3l05v-3000.inc1.devtunnels.ms/graphql";
// const URL = "https://api.tournated.com/graphql";
// const URL = "https://040208218717.ngrok-free.app/graphql"; // Ngrok
// const URL = "https://3f76-124-29-194-99.ngrok-free.app/graphql"; // AWS

const authLink = setContext(async (operation, { headers }) => {
  // While impersonating, every operation runs as the target user — except the
  // `impersonateUser` mutation itself, which must run as the real admin.
  const impersonationToken =
    operation.operationName === "impersonateUser" ? null : getImpersonationToken();
  // Non-impersonated operations get a proactively refreshed token; with no
  // refresh token stored this is just the stored access token, as before.
  const token = impersonationToken ?? (await getValidAccessToken());

  // * RETURN THE HEADERS TO THE CONTEXT SO HTTP-LINK CAN READ THEM
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
      "apollo-require-preflight": true,
      "x-app-source": "dashboard",
    },
  };
});

const httpLink = createUploadLink({
  uri: URL,
});

const sentryBreadcrumbLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    addSentryBreadcrumb({
      category: "graphql",
      message: operation.operationName || "anonymous",
      data: { hasErrors: !!result.errors?.length },
      level: result.errors?.length ? "warning" : "info",
    });
    return result;
  })
);

// Marks an operation that has already been replayed after a token refresh, so a
// still-unauthenticated replay tears the session down instead of looping.
const AUTH_RETRY_CONTEXT_KEY = "authRetry";

// * ERROR LINK TO HANDLE UNAUTHENTICATED ERRORS
const errorLink = onError(({ graphQLErrors, operation, networkError, forward }) => {
  if (graphQLErrors) {
    const isUnauthenticated = graphQLErrors.some(
      (err) => err.extensions?.code === "UNAUTHENTICATED"
    );

    if (isUnauthenticated) {
      // In an impersonated tab, an expired/invalid impersonation token must
      // only end the impersonation (reverting to the admin) — never log the
      // real admin out, broadcast a logout to their other tabs, or refresh the
      // admin's underlying tokens.
      if (isImpersonating()) {
        clearImpersonationSession();
        window.location.reload();
      } else if (operation.getContext()[AUTH_RETRY_CONTEXT_KEY] !== true && getRefreshToken()) {
        // An access token that expired mid-flight is recoverable: refresh once
        // (single-flight, shared with the axios client) and replay. The replay
        // runs back through `authLink`, which picks up the refreshed token. If
        // the refresh was rejected it already cleared the auth cells and
        // broadcast the logout, and this replay's own 401 lands in the branch
        // below because the operation is now marked as retried.
        operation.setContext({ [AUTH_RETRY_CONTEXT_KEY]: true });

        return fromPromise(refreshAccessToken()).flatMap(() => forward(operation));
      } else {
        // Terminal 401: clear EVERY auth cell (access + refresh + expiry hint),
        // not just the access token — a surviving refresh token would silently
        // resurrect the torn-down session on the next boot's bootstrap refresh.
        // `clearAuthTokens` also dispatches the `local-storage` event, keeping
        // this tab's AuthContext in sync.
        clearAuthTokens();

        try {
          broadcastLogout();
        } catch (error) {
          captureSentryException(error, { tags: { source: "broadcastLogout" } });
        }
      }
    }

    for (const err of graphQLErrors) {
      if (err.extensions?.code === "UNAUTHENTICATED") continue;

      const code = typeof err.extensions?.code === "string" ? err.extensions.code : undefined;

      if (isExpectedGraphQLError(operation.operationName, code)) continue;

      captureSentryException(new Error(`[GraphQL] ${err.message}`), {
        fingerprint: getGraphQLErrorFingerprint(operation.operationName, code, err.message),
        tags: {
          source: "graphql",
          operation: operation.operationName,
          code,
        },
        extra: {
          path: err.path,
          variables: operation.variables,
          locations: err.locations,
        },
      });
    }
  }

  if (networkError) {
    // A non-2xx GraphQL response (a 400 validation error, a 500, …) arrives here
    // as an Apollo `ServerError`, not in `graphQLErrors`. Without a fingerprint
    // Sentry groups every operation's 4xx/5xx under the shared `throwServerError`
    // stacktrace — one catch-all issue with no visible cause. Pull the status +
    // the backend's actual error out of the response body so each failure is
    // grouped per operation and the cause is finally recorded.
    const serverError = extractServerErrorInfo(networkError);

    // A 4xx can carry a GraphQL error whose (operation, code) is an expected
    // outcome the caller already handles — suppress it like the graphQLErrors path.
    if (!isExpectedGraphQLError(operation.operationName, serverError.code)) {
      captureSentryException(networkError, {
        fingerprint: getServerErrorFingerprint(operation.operationName, serverError),
        tags: {
          source: "graphql-network",
          operation: operation.operationName,
          statusCode: serverError.statusCode,
          code: serverError.code,
        },
        extra: {
          statusCode: serverError.statusCode,
          graphqlErrorCode: serverError.code,
          graphqlErrorMessage: serverError.message,
          variables: operation.variables,
        },
      });
    }
  }

  return forward(operation);
});

const client = new ApolloClient({
  // @ts-expect-error looks like some bug in types, it seems to work fine
  link: sentryBreadcrumbLink.concat(errorLink).concat(authLink).concat(httpLink),
  cache,
});

export default client;
